import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'html-react-parser';
import clsx from 'clsx';
import { Box, Typography, Card, CardHeader, CardContent, CardActions, Collapse, Popover, Button, Grid, IconButton, Tooltip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import EditIcon from '@material-ui/icons/Edit';
import ResultTableAnswers from '../ResultTableAnswers/ResultTableAnswers';
import ResultTextAnswers from '../ResultTextAnswers/ResultTextAnswers';
import ResultImageAnswers from '../ResultImageAnswers/ResultImageAnswers';
import ResultFileAnswers from '../ResultFileAnswers/ResultFileAnswers';
import Carousel from '../../Carousel/Carousel';
import ImageModule from '../../ChallengeBlock/modules/ImageModule';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import PopHover from './PopOver';
import ValidationPopover from './ValidationPopover';
import EditScorePopover from './EditScorePopover';
import StepValidatedPopover from './StepValidatedPopover';
import CheckboxEvaluationPopover from './CheckboxEvaluationPopover';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import useAppStore from '../../../../store/appStore';
import { RolesTypes } from '../../../../constants/roles';

const useStyles = makeStyles((theme) => ({
  stepRoot: {
    position:'relative',
    marginBottom:30,
    '& button': {
      '&:hover': {},
      '&:focus': {
        outline: 'none',
      },
    }
  },
  icon: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.warning.main,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
  cardContent: {
    display:'grid',
    gridTemplateColumns: '18% 18% 40% auto auto', // Layout flessibile: Score, Time, Buttons, Warning (opzionale), Validation
    alignItems:'center',
    gap: theme.spacing(1),
    paddingTop:0,
    paddingBottom:20,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      display:'flex',
      flexDirection:'column',
      gap: theme.spacing(2),
    },
  },
  stepsInfo: {
    display: 'contents', // I figli si comportano come figli diretti del grid container
    [theme.breakpoints.down('sm')]: {
      display:'flex',
      width:'100%',
      justifyContent:'center',
      flexWrap: 'wrap',
      gap: theme.spacing(3),
    },
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems:'center',
      justifyContent: 'center',
      [theme.breakpoints.down('sm')]: {
        margin:'0 10%',
      },
    }
  },
  stepScore: {
    '& .score-container': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px', // Spazio di 10px tra contenuto e icona
    },
    '& .score-content': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& .score-content div:first-child': {
      fontSize: theme.typography.pxToRem(28),
      fontWeight: theme.typography.fontWeightMedium,
    },
    '& .score-content div:last-child': {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightRegular,
      color: theme.palette.text.secondary,
      lineHeight: 1.1
    },
    '& .edit-icon': {
      cursor: 'pointer',
      color: theme.palette.text.secondary, // Stesso colore delle scritte "Score" e "Time"
      fontSize: theme.typography.pxToRem(24), 
      '&:hover': {
        color: theme.palette.text.primary, // Al hover diventa più scura
      }
    }
  },
  stepTime: {
    '& div:first-child': {
      fontSize: theme.typography.pxToRem(28),
      fontWeight: theme.typography.fontWeightMedium,
    },
    '& div:last-child': {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightRegular,
      color: theme.palette.text.secondary,
      lineHeight: 1.1
    },
    '& .step-time-number': {
      paddingLeft:5,
    },
    '& .step-time-text': {
      fontSize: theme.typography.pxToRem(18),
      paddingLeft:0.5,
    }
  },
  stepQuestion: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      order: 3, // Appare dopo Score e Time su mobile
      marginTop: theme.spacing(2),
    },
    '& button': {
      minWidth: 140,
      color: theme.palette.common.gamsDarkBlue,
      borderColor: theme.palette.common.gamsDarkBlue,
      [theme.breakpoints.down('xs')]: {
        marginTop:15,
        width: '100%',
      },
      '&:hover': {
        backgroundColor: theme.palette.common.blue[600],
        color: theme.palette.common.white,
      },
      '&:focus': {
        outline: 'none',
      },
      '&:active': {
        backgroundColor: theme.palette.common.blue[600],
        color: theme.palette.common.white,
      },
    },
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    width: '100%',
    minHeight: '60px', // Altezza minima per mantenere lo spazio
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'center',
    },
    '& .validate-button': {
      color: theme.palette.success.main,
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.common.white,
      minWidth: 140,
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
      '&:hover': {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
      },
      '&:focus': {
        outline: 'none',
      },
      '&:active': {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
      },
    }
  },
  stepValuation: {
    display: 'flex',
    flexDirection: 'column',
    alignItems:'center',
    justifyContent: 'center',
    minWidth: 0, // Permette al flex item di essere più piccolo del suo contenuto
    overflow: 'hidden', // Evita overflow
    gridColumn: 5, // Forza la colonna 4 nel layout grid
    [theme.breakpoints.down('sm')]: {
      order: 4, // Appare per ultimo su mobile
      gridColumn: 'auto',
    },
    '& svg': {
      fontSize: theme.typography.pxToRem(48), // Riduciamo leggermente la dimensione
      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(36),
      },
    },
    '& span': {
      lineHeight:1.1,
      fontSize: theme.typography.pxToRem(12),
      textAlign: 'center',
      whiteSpace: 'nowrap', // Evita che il testo vada a capo
      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(10),
        marginTop:-2
      },
    }
  },
  iconTime: {
    display: 'flex',
    flexDirection: 'column',
    alignItems:'center',
    justifyContent: 'center',
    minWidth: 0,
    gridColumn: 4, // Forza la colonna 5 nel layout grid
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      order: 5,
      gridColumn: 'auto',
      paddingLeft: 0,
      paddingTop: theme.spacing(1),
    },
    '& svg': {
      fontSize: theme.typography.pxToRem(48),
      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(36),
      },
    },
    '& span': {
      lineHeight:1.1,
      fontSize: theme.typography.pxToRem(12),
      textAlign: 'center',
      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(10),
      },
    }
  },
  iconCorrect: {
    color: theme.palette.success.main,
  },
  iconWrong: {
    color: theme.palette.error.main,
  },
  iconInValidation: {
    color: theme.palette.primary.main, // Colore blu
  },
  cardActions: {
    borderTop: `solid 1px ${theme.palette.grey[200]}`,
    '& button': {
      margin: '0 auto',
      textTransform:'none',
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightRegular,
    }
  },
  popover: {
    maxWidth:940,
    padding:theme.spacing(2)
  },
  popoverContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'top',
  },
  popoverText: {
    alignItems: 'center',
  },
  popoverCloseBtn: {
    marginLeft: '0.250rem',
  },
  imageCarousel: {
    '& .swiper-container': {
      marginTop:0,
    }
  }
}));

const ResultStep = ({ stepIndex, step, challengeId, currentStep, status, blockedSteps, onUpdateStep, isUpdating }) => {
  const classes = useStyles();
  const user = useAppStore((state) => state.user);
  const { answer, question, obtainableScore, score, time, questionType, imageCarousel, imageLink, countdownTime, documentPath, note, coachDocumentPath, evaluationCriteria } = step;
  let showWarning = false;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState("");
  const [validateAnchorEl, setValidateAnchorEl] = useState(null);
  const [editScoreAnchorEl, setEditScoreAnchorEl] = useState(null);
  const [stepValidatedAnchorEl, setStepValidatedAnchorEl] = useState(null);
  const [checkboxEvalAnchorEl, setCheckboxEvalAnchorEl] = useState(null);

  const isCoachOrAdmin = user && (user.me.userRole === RolesTypes.SUPERUSER || user.me.userRole === RolesTypes.COACH);
  const isBlockedStep = blockedSteps && blockedSteps.includes(stepIndex);
  const showCheckboxEval = evaluationCriteria === true && !isBlockedStep && isCoachOrAdmin;

  const handleCheckboxEvalClick = (event) => {
    setCheckboxEvalAnchorEl(event.currentTarget);
  };

  const handleCheckboxEvalClose = () => {
    setCheckboxEvalAnchorEl(null);
  };

  const handleConfirmCheckboxEval = (selectedNote) => {
    setCheckboxEvalAnchorEl(null);
    onUpdateStep(stepIndex, { note: selectedNote });
  };

  const handleCollapseClick = stepIndex => {
    if (selectedIndex === stepIndex) {
      setSelectedIndex("")
    } else {
      setSelectedIndex(stepIndex)
    }
  };

  const handlePopoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleValidateStep = (event) => {
    // Apre il popover di validazione invece di eseguire direttamente la validazione
    setValidateAnchorEl(event.currentTarget);
  };

  const handleValidatePopoverClose = () => {
    setValidateAnchorEl(null);
  };

  const handleConfirmValidation = (validationNotes, uploadedFile) => {
    // Controlla se lo score è 0 e mostra un alert di conferma
    if (score === 0) {
      if (window.confirm('Lo score è 0 vuoi procedere comunque con la validazione dello step?')) {
        proceedWithValidation(validationNotes, uploadedFile);
      }
      // Se l'utente ha annullato, non fare nulla (il popover rimane aperto)
    } else {
      proceedWithValidation(validationNotes, uploadedFile);
    }
  };

  // Funzione helper per eseguire la validazione
  const proceedWithValidation = (validationNotes, uploadedFile) => {
    if (onUpdateStep) {
      const updateData = {
        isValidation: true,
        note: validationNotes || undefined // Passa le note solo se presenti
      };
      
      // Aggiungi il file se presente
      if (uploadedFile) {
        updateData.attachedFile = uploadedFile; 
      }
      
      onUpdateStep(stepIndex, updateData);
    }
    
    handleValidatePopoverClose();
  };

  const handleEditClick = (event) => {
    // Apre il popover per modificare lo score
    setEditScoreAnchorEl(event.currentTarget);
    event.stopPropagation(); 
  };

  const handleEditScorePopoverClose = () => {
    setEditScoreAnchorEl(null);
  };

  const handleStepValidatedClick = (event) => {
    // Apre il popover per visualizzare note e download file del coach
    setStepValidatedAnchorEl(event.currentTarget);
  };

  const handleStepValidatedPopoverClose = () => {
    setStepValidatedAnchorEl(null);
  };

  const handleConfirmScoreEdit = (newScore) => {
    // Chiama l'endpoint PUT /results/challenge/{id} con il nuovo score
    if (onUpdateStep) {
      onUpdateStep(stepIndex, {
        score: newScore,
        isValidation: false
      });
    }
    
    handleEditScorePopoverClose();
  };

  const outOfTime = (answers) => {
    if (!answers || !Array.isArray(answers)) {
      return false;
    }
    showWarning = false;
    answers.forEach((answer) => {
      for (let i = 0; i < answers.length; i++) {
        const element = answers[i];
        let totalTime = +time.minutes * 60 + +time.seconds;
        if (element.isCorrect === true) {
          if (countdownTime) {
            if (countdownTime < totalTime) {
              showWarning = true;
            }
            else {
              showWarning = false;
            }
          } else {
              showWarning = false;
          }
        }
      }
    })
    return showWarning
  }

  const isStepCorrect = (answers) => {
    let result = true;
     if (!answers || !Array.isArray(answers)) {
      return false;
    }
    answers.forEach((answer) => {
      if (answer.isCorrect !== true) {
        result = false;
      } else {
        if(score === 0){
          showWarning = true;
        }
      }
    })
    return result;
  }

  const answerByQuestionType = (questionType, answer, documentPath) => {
    if (questionType === 'imageSelection') {
      return <ResultImageAnswers answers={answer} />;
    }
    if (questionType === 'uploadFile') {
      return <ResultFileAnswers answers={answer} documentPath={documentPath} />;
    }
    return <ResultTextAnswers answers={answer} />;
  };

  const getStepValidationIcon = () => {
    // Se siamo sullo step corrente E la challenge è in validazione, mostra l'icona del punto interrogativo blu
    if (stepIndex === currentStep && status === 'IN_VALIDATION') {
      return {
        icon: <HelpOutlineIcon className={classes.iconInValidation} />,
        text: 'In validation'
      };
    }
    
    // Se lo step è in blockedSteps ma NON è il currentStep, significa che è già stato validato
    if (blockedSteps && blockedSteps.includes(stepIndex) && stepIndex !== currentStep) {
      return {
        icon: <DoneIcon className={classes.iconCorrect} />,
        text: 'Validated'
      };
    }
    
    // Altrimenti usa la logica esistente (Correct/Wrong)
    const isCorrect = isStepCorrect(answer);
    return {
      icon: isCorrect ? <DoneIcon className={classes.iconCorrect} /> : <CloseIcon className={classes.iconWrong} fontSize="large" />,
      text: isCorrect ? 'Correct' : 'Wrong'
    };
  };

  const popoverOpen = Boolean(anchorEl);
  const popoverId = popoverOpen ? 'simple-popover' : undefined;
  
  const validatePopoverOpen = Boolean(validateAnchorEl);

  return (
    <Card className={classes.stepRoot}>

      <CardHeader
        classes={{
          title: classes.cardTitle
        }}
        title={`Step ${stepIndex + 1}`}
      />

      <CardContent className={classes.cardContent}>
        <Box className={classes.stepsInfo}>
          <div className={classes.stepScore}>
            <div className="score-container">
              <div className="score-content">
                <Typography variant="body1" component="div">{score}/{ obtainableScore } </Typography>
                <Typography variant="body1" component="div">Score</Typography>
              </div>
              {user && (user.me.userRole === RolesTypes.SUPERUSER || user.me.userRole === RolesTypes.COACH) && (
                <Tooltip title="Edit score" arrow>
                  <EditIcon 
                    className="edit-icon"
                    onClick={handleEditClick}
                  />
                </Tooltip>
              )}
            </div>
          </div>
          <div className={classes.stepTime}>
            <Typography variant="body1" component="div">
              <span className={'step-time-number'}>{time.minutes}</span><span className={'step-time-text'}>m</span>
              <span className={'step-time-number'}>{time.seconds}</span><span className={'step-time-text'}>s</span>
            </Typography>
            <Typography variant="body1" component="div">Time</Typography>
          </div>
          <div className={classes.stepQuestion}>
            <Box className={classes.buttonsContainer}>
              {question && (
                <Button
                  variant="outlined"
                  aria-describedby={popoverId}
                  onClick={handlePopoverClick}
                  startIcon={<InfoOutlinedIcon />}
                >
                  Step question
                </Button>
              )}
              {stepIndex === currentStep && status === 'IN_VALIDATION' && (
                isUpdating ? (
                  <LoadingSpinner styles={{ minHeight: '36px', padding: '8px 0' }} />
                ) : (
                  <Button
                    variant="outlined"
                    className="validate-button"
                    onClick={handleValidateStep}
                    startIcon={<CheckCircleOutlineIcon />}
                  >
                    Valida Step
                  </Button>
                )
              )}
              {stepIndex !== currentStep && isBlockedStep && isCoachOrAdmin && (
                <Button
                  variant="outlined"
                  className="validate-button"
                  disabled={(note && note.trim() !== '') || (coachDocumentPath && coachDocumentPath.trim() !== '') ? false : true}
                  onClick={(note && note.trim() !== '') || (coachDocumentPath && coachDocumentPath.trim() !== '') ? handleStepValidatedClick : undefined}
                  startIcon={
                    (note && note.trim() !== '') || (coachDocumentPath && coachDocumentPath.trim() !== '') ?
                      <InfoOutlinedIcon /> : null
                  }
                >
                  Step Validato
                </Button>
              )}
              {showCheckboxEval && (
                <Button
                  variant="outlined"
                  className="validate-button"
                  onClick={handleCheckboxEvalClick}
                  startIcon={<CheckCircleOutlineIcon />}
                >
                  {note && note.trim() !== '' ? 'Evaluated Checkbox' : 'Checkbox Evaluation'}
                </Button>
              )}
            </Box>
            {question && (
              <Popover
                id={popoverId}
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                classes={{
                  paper: classes.popover
                }}
              >
                <Box className={classes.popoverContent}>
                  <Grid container spacing={3} className={classes.popoverText}>
                    <Grid item xs={12} sm={imageLink !== null ? 7 : 12}>
                      <Typography component="div">
                        {parse(question)}
                      </Typography>
                    </Grid>
                    { imageLink !== null && (
                    <Grid item xs={12} sm={5}>
                      <ImageModule
                        imgSrc={imageLink}
                        ImgAlt="image module alt"
                      />
                    </Grid>
                    )}
                    { imageCarousel !== null && (
                    <Grid className={classes.imageCarousel} item xs={12}>
                      <Carousel carouselSlides={imageCarousel} />
                    </Grid>
                    )}
                  </Grid>
                  <Box className={classes.popoverCloseBtn}>
                    <IconButton 
                      component="span"
                      onClick={handlePopoverClose}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Popover>
            )}
            
            {/* Componente ValidationPopover */}
            <ValidationPopover
              open={validatePopoverOpen}
              anchorEl={validateAnchorEl}
              onClose={handleValidatePopoverClose}
              onConfirm={handleConfirmValidation}
              stepIndex={stepIndex}
              evaluationCriteria={evaluationCriteria}
              maxScore={obtainableScore}
            />

            {/* Componente EditScorePopover  */}
            <EditScorePopover
              open={Boolean(editScoreAnchorEl)}
              anchorEl={editScoreAnchorEl}
              onClose={handleEditScorePopoverClose}
              onConfirm={handleConfirmScoreEdit}
              stepIndex={stepIndex}
              currentScore={score}
              maxScore={obtainableScore}
            />

            {/* Componente StepValidatedPopover */}
            <StepValidatedPopover
              open={Boolean(stepValidatedAnchorEl)}
              anchorEl={stepValidatedAnchorEl}
              onClose={handleStepValidatedPopoverClose}
              stepIndex={stepIndex}
              note={note}
              coachDocumentPath={coachDocumentPath}
            />

            {/* Componente CheckboxEvaluationPopover */}
            <CheckboxEvaluationPopover
              open={Boolean(checkboxEvalAnchorEl)}
              anchorEl={checkboxEvalAnchorEl}
              onClose={handleCheckboxEvalClose}
              onConfirm={handleConfirmCheckboxEval}
              stepIndex={stepIndex}
              maxScore={obtainableScore}
              note={note}
            />
          </div>
        </Box>
         <Box className={classes.iconTime} style={{ visibility: outOfTime(answer) ? 'visible' : 'hidden' }}>
          <PopHover text="It's correct, but you're out of time">
            <WarningIcon className={classes.icon}/>
          </PopHover>
        </Box>
        <Box className={classes.stepValuation}>
          {getStepValidationIcon().icon}
          <Typography variant="body1" component="span" color="textSecondary">{getStepValidationIcon().text}</Typography>
        </Box>
       
      </CardContent>
      <CardActions className={classes.cardActions} disableSpacing>
        <Button
          onClick={() => { handleCollapseClick(stepIndex) }}
          aria-expanded={stepIndex === selectedIndex}
          endIcon={<ExpandMoreIcon className={clsx(classes.expand, {
            [classes.expandOpen]: stepIndex === selectedIndex,
          })} />}
        >
          View answers
        </Button>
      </CardActions>
      <Collapse in={stepIndex === selectedIndex} timeout="auto" unmountOnExit>
        <CardContent className={classes.cardContentCollapse}>
          { question === null ? <ResultTableAnswers answers={answer}/> : answerByQuestionType(questionType, answer, documentPath) }
        </CardContent>
      </Collapse>

    </Card>
  );
};

export default ResultStep;
